import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { EASE, scrollToHash } from "../lib/motion";
import { IMAGES, WA_DEFAULT, WHATSAPP_DISPLAY } from "../data/site";

const LINES = [
  { text: "Every East & West", gold: false },
  { text: "locality between", gold: false },
  { text: "Andheri – Borivali.", gold: true },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/api/projects")).data,
  });
  const count = projects?.length;

  return (
    <section ref={ref} id="top" className="relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_75%_10%,rgba(201,162,39,0.12),transparent_55%)]"
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-32 sm:px-8 md:pt-40 lg:grid-cols-[1.05fr_0.95fr] lg:pb-28">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
            className="mb-7 flex items-center gap-4"
          >
            <span className="gold-rule" />
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-gold-deep sm:text-xs">
              Real Estate Advisory — Mumbai Western Suburbs
            </p>
          </motion.div>

          <h1 className="font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-[3.75rem]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.gold ? "text-gold-gradient pr-2 italic" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.2 + i * 0.13 }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-navy/70 sm:text-lg"
          >
            Shortlisted, live and verified homes and commercial properties,
            negotiated for you. One WhatsApp message starts the journey of
            transparent and value-added investment for you and your family.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.8 }}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-hero-cta"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-wa px-7 py-4 text-base font-bold text-white shadow-[0_8px_24px_rgba(31,175,94,0.35)] transition-all hover:bg-[#189a51]"
            >
              <MessageCircle className="h-5 w-5 transition-transform group-hover:scale-110" />
              Chat on WhatsApp
            </a>
            <button
              data-testid="hero-explore-cta"
              onClick={() => scrollToHash("#inventory")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 bg-white/60 px-7 py-4 text-base font-semibold text-navy transition-all hover:border-gold-deep hover:text-gold-deep"
            >
              Browse live listings
              {count ? (
                <span className="rounded-full bg-navy px-2.5 py-0.5 text-xs font-bold text-white" data-testid="hero-listing-count">
                  {count}
                </span>
              ) : null}
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-semibold uppercase tracking-[0.16em] text-navy/50 sm:text-[13px]"
          >
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-wa" />
              Portals-tracked inventory
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#E40046]" data-testid="badge-housing" />
              Housing.com
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4A90D9]" data-testid="badge-acres" />
              99acres
            </span>
            <span>East &amp; West sides</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.35 }}
          className="relative"
        >
          <motion.div style={{ y: bgY }} className="relative">
            <div className="overflow-hidden rounded-[2rem] border border-[#E7E3D8] shadow-[0_24px_60px_rgba(14,32,56,0.14)]">
              <img
                src={IMAGES.hero}
                alt="Mumbai western suburbs coastline with residential towers"
                className="aspect-[4/3.4] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 left-5 right-5 flex items-center justify-between gap-4 rounded-2xl border border-[#E7E3D8] bg-white/95 px-5 py-4 shadow-[0_16px_40px_rgba(14,32,56,0.16)] backdrop-blur-md sm:left-8 sm:right-auto">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-deep">
                  Coverage
                </p>
                <p className="mt-1 font-serif text-lg font-semibold leading-tight text-navy">
                  Borivali ↔ Andheri · East &amp; West
                </p>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy font-serif text-sm font-semibold text-gold-light">
                SC
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
